class LazyScriptLoader {

  // scripts is an array of objects.
  // Each object has selector, src, callback, and optional rootMargin/threshold properties.
  constructor(scripts) {
    if (scripts.length <= 0) return;
    this.scripts = scripts;
    this.loadedScripts = new Set();
    this.observers = new Map();
    this.setupObservers();
  }

  setupObservers() {
    this.scripts.forEach(script => {
      const elements = document.querySelectorAll(script.selector);
      if (elements.length <= 0) return;

      // Check if IntersectionObserver is supported
      if (!('IntersectionObserver' in window)) {
        // Fallback: load script immediately
        this.insertScript(script);
        return;
      }

      // Create IntersectionObserver with custom options or defaults
      const options = {
        rootMargin: script.rootMargin || '150px',
        threshold: script.threshold || 0.1
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.insertScript(script);
            // Stop observing this element after script is loaded
            observer.unobserve(entry.target);
          }
        });
      }, options);

      // Observe all matching elements
      elements.forEach(element => {
        observer.observe(element);
      });

      // Store observer reference for cleanup
      this.observers.set(script.selector, observer);
    });
  }

  insertScript(script) {
    // 1. Check if script has already been inserted.
    if (this.loadedScripts.has(script.src)) {
      // Script already loaded, just run callback
      if (script.callback) {
        script.callback();
      }
      return;
    }

    const existingScript = document.querySelector(`script[src="${script.src}"]`);

    if (existingScript) {
      this.loadedScripts.add(script.src);
      let existingCallback = existingScript.onload;

      existingScript.onload = () => {
        if (script.callback) script.callback();
        if (existingCallback) existingCallback();
      };
      existingScript.onreadystatechange = () => {
        if (script.callback) script.callback();
        if (existingCallback) existingCallback();
      };
      return;
    }

    // 2. Create the script element.
    let newScript = document.createElement('script');
    newScript.onload = () => {
      this.loadedScripts.add(script.src);
      if (script.callback) script.callback();
    };
    newScript.onreadystatechange = () => {
      this.loadedScripts.add(script.src);
      if (script.callback) script.callback();
    };
    newScript.type = 'text/javascript';
    newScript.async = true;
    newScript.src = script.src;

    // 3. Append script to the body.
    let body = document.querySelector('body');
    body.appendChild(newScript);
  }

  // Cleanup method to disconnect all observers
  disconnect() {
    this.observers.forEach(observer => {
      observer.disconnect();
    });
    this.observers.clear();
  }
}
